/**
 * Shared utilities for client-side document upload and text extraction.
 * Used by IdeaChatInput.
 */

export type SupportedFileKind = "pdf" | "docx" | "pptx" | "txt" | "md";

const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const PPTX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const MARKDOWN_MIME_TYPES = new Set(["text/markdown", "text/x-markdown"]);

/** 50 MB — maximum file size for document uploads. */
export const DOCUMENT_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

export type FileSizeBucket = "<5mb" | "5-15mb" | "15-30mb";

/** Bucket a file size into an analytics-safe string. Never the raw byte count (analytics conventions). */
export function fileSizeBucket(bytes: number): FileSizeBucket {
  const mb = bytes / (1024 * 1024);
  if (mb < 5) return "<5mb";
  if (mb < 15) return "5-15mb";
  return "15-30mb";
}

/** `accept` attribute value covering all supported document formats. */
export const DOCUMENT_ACCEPT_STRING = [
  "application/pdf",
  ".docx",
  DOCX_MIME_TYPE,
  ".pptx",
  PPTX_MIME_TYPE,
  ".txt",
  "text/plain",
  ".md",
  ".markdown",
  "text/markdown",
  "text/x-markdown",
].join(",");

/**
 * Classify a File into a supported document kind, or return null if unsupported.
 */
export function getDocumentKind(file: File): SupportedFileKind | null {
  const name = file.name.toLowerCase();
  if (file.type.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (file.type === DOCX_MIME_TYPE || name.endsWith(".docx")) return "docx";
  if (file.type === PPTX_MIME_TYPE || name.endsWith(".pptx")) return "pptx";
  if (MARKDOWN_MIME_TYPES.has(file.type) || name.endsWith(".md") || name.endsWith(".markdown"))
    return "md";
  if (file.type === "text/plain" || name.endsWith(".txt")) return "txt";
  return null;
}

/**
 * Strip null bytes and invalid UTF-8 sequences from text extracted from uploaded files.
 */
export function sanitizeUploadedText(text: string): string {
  let sanitized = text.replace(/\0/g, "");
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder("utf-8", { fatal: false });
    sanitized = decoder.decode(encoder.encode(sanitized));
  } catch {
    /* benign: TextDecoder may fail with invalid UTF-8; fallback regex used */
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, "");
  }
  return sanitized;
}

/**
 * Read a TXT or MD file as UTF-8 text, sanitize, and return it.
 * Throws if no readable text is found.
 */
export async function parseTextLikeFile(
  file: File,
  fileKind: "txt" | "md",
): Promise<string> {
  const buffer = await file.arrayBuffer();
  const text = sanitizeUploadedText(
    new TextDecoder("utf-8", { fatal: false }).decode(buffer),
  );
  if (!text.trim()) {
    throw new Error(`No text could be extracted from the ${fileKind.toUpperCase()}`);
  }
  return text;
}
